import { selector, selectorFamily } from "recoil"
import { legalEntitiesState } from "../store/legalEntities.store"
import { Entity, FolderTree } from "../../api-client-nswag/custompermission-client"
import { areArraysEqual } from "../../util/Utils"

export const entitiesSelector = selector({
  key: "legalEntities/entities",
  get: ({ get }) => get(legalEntitiesState).entities,
})

export const usersPerSiteSelector = selector({
  key: "legalEntities/users",
  get: ({ get }) => get(legalEntitiesState).usersPerSite,
})

export const snapshotEntitiesSelector = selector({
  key: "legalEntities/snapshotEntities",
  get: ({ get }) => get(legalEntitiesState).snapshotEntities,
})

export const folderPermissionSelector = selector({
  key: "legalEntities/folderPermission",
  get: ({ get }) => get(legalEntitiesState).folderPermission,
})

export const folderPermissionSnapshotSelector = selector({
  key: "legalEntities/folderPermissionSnapshot",
  get: ({ get }) => get(legalEntitiesState).folderPermissionSnapshot,
})

export const buttonDisabledSelector = selector({
  key: "legalEntities/buttonDisable",
  get: ({ get }) => get(legalEntitiesState).isButtonDisabled,
});

export const isScheduleActionDisabledSelector = selector({
  key: "legalEntities/isScheduleActionDisabled",
  get: ({ get }) => {
    const isButtonDisabled = get(buttonDisabledSelector);
    const isDirty = get(isLegalEntityDirtySelector);
    
    return isButtonDisabled || isDirty;
  },
});

export const userNameByIdSelector = selectorFamily<string | undefined, string>({
  key: 'legalEntities/userNameById',
  get: (userId: string) => ({ get }) => {
    const users = get(usersPerSiteSelector) as Array<{ userId?: string; username?: string }>;
    if (!Array.isArray(users) || !userId) return undefined;

    const u = users.find((x) => String(x?.userId ?? '') === String(userId));
    return u?.username;
  },
});

export const entityFolderPermissionSelector = selectorFamily<FolderTree, number>({
  key: "legalEntities/folderPermission",
  get: (entityId: number) => ({ get }) => {
    const { folderPermission } = get(legalEntitiesState);

    if (!Array.isArray(folderPermission) || folderPermission.length === 0)
      return [];

    const root = folderPermission[0];

    if (!Array.isArray(root.children)) return [];

    const entityNode = root.children.find(
      (e) => e.entityId === entityId
    );

    if (!entityNode) return [];

    return [entityNode];
  },
});

export const entitySnapshotFolderTreeSelector = selectorFamily<FolderTree, number>({
  key: "legalEntities/entitySnapshotFolderTree",
  get: (entityId: number) => ({ get }) => {
    const { entities } = get(legalEntitiesState)

    const entity = entities.find((e) => e.entityId === entityId)
    if (!entity) return []

    return entity.folderTree
  },
})

export const isLegalEntityDirtySelector = selector({
  key: "legalEntities/isLegalEntityDirty",
  get: ({ get }) => {
    const { entities, snapshotEntities, folderPermission, folderPermissionSnapshot } = get(legalEntitiesState)

    const snapshotMap = new Map(
      snapshotEntities.map((e) => [e.entityId, e.userList])
    )

    for (const entity of entities) {
      const originalUserList = snapshotMap.get(entity.entityId) || []

      const currentUserList = entity.userList || []

      if (!areArraysEqual(originalUserList, currentUserList)) {
        return true
      }

      if (entity.isRestricted !== snapshotEntities.find(e => e.entityId === entity.entityId)?.isRestricted) {
        return true
      }
    }

    const makeUsersMap = (tree: FolderTree) => {
      const map = new Map<number, string[]>();
      const stack = [...tree];
      while (stack.length) {
        const node = stack.pop()!;
        map.set(node.folderId, node.users ?? []);
        if (Array.isArray(node.children) && node.children.length) {
          stack.push(...node.children);
        }
      }
      return map;
    };

    const snapMap = makeUsersMap(folderPermissionSnapshot);
    const currMap = makeUsersMap(folderPermission);

    let dirty = false;

    currMap.forEach((users, folderId) => {
      const originalUsers = snapMap.get(folderId) ?? [];
      if (!areArraysEqual(users, originalUsers)) {
        dirty = true;
      }
    });

    snapMap.forEach((_users, folderId) => {
      if (!currMap.has(folderId)) {
        dirty = true;
      }
    });

    return dirty
  },
})

